import { z } from 'zod';

/**
 * Entity-level mutation op payloads (IV.E.2 / #88).
 *
 * The Walking-Skeleton substrate shipped fact-level soft-delete (via
 * `fact.soft_delete` + `fact.restore` in `./fact-ops.ts`). This module
 * adds the matching **entity-level** soft-delete + restore ops, plus
 * a cascading projection helper that hides an entity's facts when the
 * entity itself is soft-deleted. The op log is the source of truth;
 * soft-delete is an op, never a column rewrite.
 *
 * Cascade semantics (per `02-architecture.md §4.2` + `30-design-system.md
 * §9.2` Trash surface):
 *
 *   - `entity.soft_delete` hides the entity AND all of its facts from
 *     the (GM) projection.
 *   - `entity.restore` un-hides them.
 *   - Fact-level `fact.soft_delete` / `fact.restore` ops remain
 *     authoritative for per-fact lifecycle; the entity flag is an
 *     additional gate ORed on top.
 *   - Hard-delete (purge of soft-deleted entities after a grace window)
 *     is configured but not executed in Phase 1 — see
 *     `ENTITY_SOFT_DELETE_GRACE_DAYS` for the planned default.
 *
 * Phase 2 surfaces a unified "Trash" view that lists soft-deleted
 * entities + facts and exposes restore / hard-delete affordances. This
 * module is the substrate-only piece that Phase 2 will read from.
 *
 * The ops are appended through the existing `__entity` reserved
 * predicate slot — the same chain as `entity.create` (see
 * `entities-fold.ts §ENTITY_META_PREDICATE`). The fold updates a new
 * `soft_deleted: boolean` flag on the resulting `EntityState`.
 */

/**
 * Number of days a soft-deleted entity remains recoverable before the
 * background purge job is eligible to remove it. Phase 1 only declares
 * the window; the purge job lands in Phase 3.
 *
 * 30 days matches the `30-design-system.md §9.2` Trash window and the
 * standard SaaS expectation for accidental-delete recovery.
 */
export const ENTITY_SOFT_DELETE_GRACE_DAYS = 30 as const;

/**
 * Trait attach / detach ops (#139).
 *
 * The Walking-Skeleton substrate let `entity.create` set an initial
 * trait list and then never mutated it. #139 adds the missing trait
 * lifecycle ops so the GM can attach or detach a trait at any later
 * time via the same op-log append path.
 *
 * Semantics:
 *
 *   - `entity.attach_trait` adds `trait_id` to the entity's trait set
 *     unless it is already present (idempotent). Attaching a trait
 *     surfaces every predicate the trait declares in the fact-row
 *     editor; the entity does not start carrying any *facts* on those
 *     predicates — the GM fills them in.
 *   - `entity.detach_trait` removes `trait_id` from the entity's trait
 *     set. Per the substrate contract, **facts on the trait's
 *     predicates are NOT mutated**; they persist in the op log and the
 *     fold continues to roll them up. The editor simply stops rendering
 *     the corresponding rows because the predicate is no longer
 *     reachable through the entity's trait set. Re-attaching the trait
 *     restores the editor rows with their existing values intact.
 *
 * Per the progressive-canonicity principle (`00-vision.md §1`) no
 * trait is required by any root — `applies_to` constrains *which*
 * roots a trait can attach to, but never *requires* one. These ops
 * make the lifecycle explicit at the op-log layer.
 *
 * The fold (entities-fold.ts) applies the ops idempotently — replaying
 * an attach for a trait already present is a no-op; replaying a detach
 * for a trait not present is a no-op. This keeps the projection
 * rebuildable from the raw op stream without any companion state.
 */
export const EntityAttachTraitPayloadSchema = z
  .object({
    kind: z.literal('entity.attach_trait'),
    trait_id: z.string().min(1),
  })
  .strict();
export type EntityAttachTraitPayload = z.infer<typeof EntityAttachTraitPayloadSchema>;

export const EntityDetachTraitPayloadSchema = z
  .object({
    kind: z.literal('entity.detach_trait'),
    trait_id: z.string().min(1),
  })
  .strict();
export type EntityDetachTraitPayload = z.infer<typeof EntityDetachTraitPayloadSchema>;

/** Build an attach-trait payload (#139). */
export function entityAttachTrait(traitId: string): EntityAttachTraitPayload {
  return { kind: 'entity.attach_trait', trait_id: traitId };
}

/** Build a detach-trait payload (#139). */
export function entityDetachTrait(traitId: string): EntityDetachTraitPayload {
  return { kind: 'entity.detach_trait', trait_id: traitId };
}

/** Type guard — attach-trait payload (#139). */
export function isEntityAttachTrait(payload: unknown): payload is EntityAttachTraitPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'entity.attach_trait'
  );
}

/** Type guard — detach-trait payload (#139). */
export function isEntityDetachTrait(payload: unknown): payload is EntityDetachTraitPayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'entity.detach_trait'
  );
}

export const EntitySoftDeletePayloadSchema = z
  .object({
    kind: z.literal('entity.soft_delete'),
    /** Optional GM-supplied reason text recorded for audit. */
    reason: z.string().min(1).optional(),
  })
  .strict();
export type EntitySoftDeletePayload = z.infer<typeof EntitySoftDeletePayloadSchema>;

export const EntityRestorePayloadSchema = z
  .object({
    kind: z.literal('entity.restore'),
    reason: z.string().min(1).optional(),
  })
  .strict();
export type EntityRestorePayload = z.infer<typeof EntityRestorePayloadSchema>;

/** Build an entity soft-delete payload (op log mutation, not a column write). */
export function entitySoftDelete(reason?: string): EntitySoftDeletePayload {
  return reason === undefined
    ? { kind: 'entity.soft_delete' }
    : { kind: 'entity.soft_delete', reason };
}

/** Build an entity restore payload — reverses a prior entity soft-delete. */
export function entityRestore(reason?: string): EntityRestorePayload {
  return reason === undefined ? { kind: 'entity.restore' } : { kind: 'entity.restore', reason };
}

/** Type guard for entity-soft-delete/restore payloads (used by the entities-fold). */
export function isEntitySoftDelete(payload: unknown): payload is EntitySoftDeletePayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'entity.soft_delete'
  );
}

export function isEntityRestore(payload: unknown): payload is EntityRestorePayload {
  return (
    typeof payload === 'object' &&
    payload !== null &&
    (payload as { kind?: unknown }).kind === 'entity.restore'
  );
}
